import type {
  DecodedFitFile,
  DecodeError,
  MixedMessageUnknownFields,
  PreservedUnknownMessage,
} from './raw-fit.js'

/**
 * The `Activity` domain model: a typed, convenient facade over a
 * `DecodedFitFile`'s type-grouped `messages` dictionary, per
 * ARCHITECTURE.md §2/§5 and
 * `openspec/changes/define-fit-core-specs/design.md`'s "plain,
 * Immer-draftable object tree, not classes" decision.
 *
 * Deliberately stays structurally close to the underlying FIT messages
 * (rather than inventing a parallel, hand-rolled shape) so
 * `activityToDecoded` can rebuild a `DecodedFitFile` losslessly — the
 * value this layer adds is named/typed access to the message types the
 * editing engine actually operates on, not a reinvention of the format.
 * Every record type allows arbitrary extra keys (`[key: string]: unknown`)
 * so unrecognized-but-captured fields (see `raw-fit.ts`) and any FIT
 * field not worth a named property yet still round-trip untouched.
 */

export interface FitMessage {
  [key: string]: unknown
}

export interface ActivityRecord extends FitMessage {
  timestamp?: Date
  positionLat?: number
  positionLong?: number
  altitude?: number
  heartRate?: number
  cadence?: number
  power?: number
  temperature?: number
  speed?: number
  distance?: number
  developerFields?: Record<string, unknown>
}

export interface ActivityLap extends FitMessage {
  timestamp?: Date
  startTime?: Date
  totalElapsedTime?: number
  totalTimerTime?: number
  totalDistance?: number
}

export interface ActivitySession extends FitMessage {
  timestamp?: Date
  startTime?: Date
  sport?: string
  totalElapsedTime?: number
  totalTimerTime?: number
  totalDistance?: number
}

export interface ActivityEvent extends FitMessage {
  timestamp?: Date
  event?: string
  eventType?: string
}

export interface ActivityFileId extends FitMessage {
  type?: string
  manufacturer?: string
  product?: number
  serialNumber?: number
  timeCreated?: Date
}

export interface ActivitySummary extends FitMessage {
  timestamp?: Date
  numSessions?: number
  type?: string
}

/**
 * Message types not yet promoted to a named field above (sport,
 * device_info, length, developer_data_id, field_description, and any
 * other recognized type a fixture turns out to contain), keyed by the
 * SDK's `messagesKey` (e.g. `"sportMesgs"`).
 */
export type OtherMessages = Record<string, FitMessage[]>

export interface Activity {
  fileId: ActivityFileId | undefined
  records: ActivityRecord[]
  laps: ActivityLap[]
  sessions: ActivitySession[]
  events: ActivityEvent[]
  activitySummary: ActivitySummary | undefined
  otherMessages: OtherMessages
  preservedUnknownMessages: PreservedUnknownMessage[]
  mixedMessageUnknownFields: MixedMessageUnknownFields[]
  decodeErrors: DecodeError[]
}

const NAMED_KEYS = {
  fileId: 'fileIdMesgs',
  records: 'recordMesgs',
  laps: 'lapMesgs',
  sessions: 'sessionMesgs',
  events: 'eventMesgs',
  activitySummary: 'activityMesgs',
} as const

/** Builds the domain model from a decode result. Never mutates `decoded`. */
export function activityFromDecoded(decoded: DecodedFitFile): Activity {
  const otherMessages: OtherMessages = {}
  for (const [key, instances] of Object.entries(decoded.messages)) {
    if (Object.values(NAMED_KEYS).includes(key as (typeof NAMED_KEYS)[keyof typeof NAMED_KEYS])) {
      continue
    }
    otherMessages[key] = instances as FitMessage[]
  }

  return {
    fileId: decoded.messages[NAMED_KEYS.fileId]?.[0] as ActivityFileId | undefined,
    records: (decoded.messages[NAMED_KEYS.records] ?? []) as ActivityRecord[],
    laps: (decoded.messages[NAMED_KEYS.laps] ?? []) as ActivityLap[],
    sessions: (decoded.messages[NAMED_KEYS.sessions] ?? []) as ActivitySession[],
    events: (decoded.messages[NAMED_KEYS.events] ?? []) as ActivityEvent[],
    activitySummary: decoded.messages[NAMED_KEYS.activitySummary]?.[0] as
      ActivitySummary | undefined,
    otherMessages,
    preservedUnknownMessages: decoded.preservedUnknownMessages,
    mixedMessageUnknownFields: decoded.mixedMessageUnknownFields,
    decodeErrors: decoded.errors,
  }
}

/**
 * Rebuilds a `DecodedFitFile` from the domain model, for handing to
 * `encodeFitFile`. `fieldDefsByMesgNum` is required by the preservation
 * layer for anything in `preservedUnknownMessages`/
 * `mixedMessageUnknownFields` — pass through the value `decodeFitFile`
 * returned; editing operations that don't touch preserved content never
 * need to modify it.
 */
export function activityToDecoded(
  activity: Activity,
  fieldDefsByMesgNum: DecodedFitFile['fieldDefsByMesgNum'],
): DecodedFitFile {
  const messages: DecodedFitFile['messages'] = { ...activity.otherMessages }
  if (activity.fileId != null) messages[NAMED_KEYS.fileId] = [activity.fileId]
  if (activity.records.length > 0) messages[NAMED_KEYS.records] = activity.records
  if (activity.laps.length > 0) messages[NAMED_KEYS.laps] = activity.laps
  if (activity.sessions.length > 0) messages[NAMED_KEYS.sessions] = activity.sessions
  if (activity.events.length > 0) messages[NAMED_KEYS.events] = activity.events
  if (activity.activitySummary != null) {
    messages[NAMED_KEYS.activitySummary] = [activity.activitySummary]
  }

  return {
    messages,
    fieldDefsByMesgNum,
    preservedUnknownMessages: activity.preservedUnknownMessages,
    mixedMessageUnknownFields: activity.mixedMessageUnknownFields,
    errors: activity.decodeErrors,
  }
}
